import * as React from "react";
import { Form } from "semantic-ui-react";
import { Input } from "@neworbit/simpleui-input";
import { FormBaseComponent, SaveDispatchProps } from "@neworbit/simpleui-forms";

import { FOLLOW_UP_ACTIONS_OPTIONS, FollowUpTaskModel } from "../model";
import { ImmunisationTypeTask } from "../../crud/immunisationTypeTask";
import { getUserDisplayNameWithTeamAndProfession } from "../../crud/adminUserWithTeams/model";
import { AdminUserListModelWithTeams } from "../../crud/adminUserWithTeams/model";
import { ImmunisationType } from "../../crud/immunisationType";

interface OwnProps {
    showErrors: boolean;
    task: FollowUpTaskModel;
    immunisationTypeTasks: ImmunisationTypeTask[];
    onChange: (task: FollowUpTaskModel, isValid: boolean) => void;
    restrictToTaskTypes?: string[];
    multiple?: boolean;
    hideAssignmentOption?: boolean;
    assignmentOptionRequired?: boolean;
    adminUsers?: AdminUserListModelWithTeams[];
    immunisationTypes: ImmunisationType[];
    showImmunisationNameAndType?: boolean;
}

export class FollowUpTaskForm extends FormBaseComponent<FollowUpTaskModel, OwnProps & SaveDispatchProps<FollowUpTaskModel>> {
    constructor(props: OwnProps & SaveDispatchProps<FollowUpTaskModel>) {
        super(props);

        this.state = {
            values: props.task,
            valid: {
                taskType: false,
                immunisationTypeTasks: false,
                dueDate: false,
                assignedUsers: props.assignmentOptionRequired ? false : true
            }
        };
    }

    public componentDidUpdate(prevProps: OwnProps) {
        if (this.props.task !== prevProps.task) {
            this.setState({ values: this.props.task });
        }
    }

    private setAssignedUser = (assignedUserId: string, isValid: boolean) => {
        const adminUser = this.props.adminUsers.find(a => a.id === assignedUserId);
        this.setState({
            values: { ...this.state.values, assignedUsers: adminUser ? [{ id: adminUser.id, name: getUserDisplayNameWithTeamAndProfession(adminUser) }] : [] },
            valid: {
                ...this.state.valid,
                assignedUsers: isValid
            }
        }, () => this.props.onChange(this.state.values, this.isValid()));
    };

    private isValid = (): boolean => {
        return Object.values(this.state.valid).every(v => v);
    };

    public render() {
        const immunisationTaskType = this.state.values.taskType && FOLLOW_UP_ACTIONS_OPTIONS.find(o => o.value === this.state.values.taskType)?.taskType;
        const immunisationTasks = this.props.immunisationTypeTasks
            .filter(t => t.taskType === immunisationTaskType)
            .map(task => ({ key: task.id, value: task.id, text: task.name, immunisationTypeId: task.immunisationTypeId }));
        const followUpAction = this.state.values.taskType && FOLLOW_UP_ACTIONS_OPTIONS.find(o => o.value === this.state.values.taskType);
        const associatedWithImmunisation =
            followUpAction && followUpAction.associatedWithImmunisation;
        const dueDateApplicable =
            followUpAction && followUpAction.dueDateApplicable;

        const options = FOLLOW_UP_ACTIONS_OPTIONS
            .map(o => ({ value: o.value, text: o.text }))
            .filter(t => !this.props.restrictToTaskTypes || this.props.restrictToTaskTypes.some(includedTask => includedTask === t.value))
            .sort((a, b) => a.text.localeCompare(b.text));

        return (
            <>
                <Form.Field>
                    <Input.Dropdown
                        placeholder="Select action *"
                        dynamicOptions={true}
                        options={options}
                        value={this.state.values.taskType}
                        required
                        showErrors={this.props.showErrors}
                        onChange={(value, isValid) => {
                            const newTask = FOLLOW_UP_ACTIONS_OPTIONS.find(a => a.value === value);
                            const newTaskAssociatedWithImmunisation = newTask && newTask.associatedWithImmunisation;
                            this.setState(current => ({
                                values: {
                                    ...current.values,
                                    taskType: value,
                                    immunisationTypeTasks: newTaskAssociatedWithImmunisation ? [] : null,
                                    dueDate: !current.values.dueDate ? null : current.values.dueDate
                                },
                                valid: { ...current.valid, taskType: isValid, immunisationTypeTasks: !newTaskAssociatedWithImmunisation, dueDate: true }
                            }), () => this.props.onChange(this.state.values, this.isValid()));
                        }}
                    />
                </Form.Field>
                {dueDateApplicable &&
                    <Form.Field>
                        <Input.Date
                            label="Due Date *"
                            required
                            onChange={(value, isValid) =>
                                this.setState(
                                    current => ({ values: { ...current.values, dueDate: value }, valid: { ...current.valid, dueDate: isValid } }),
                                    () => this.props.onChange(this.state.values, this.isValid())
                                )
                            }
                            value={this.state.values.dueDate}
                            showErrors={this.props.showErrors}
                        />
                    </Form.Field>
                }
                {associatedWithImmunisation &&
                    (this.props.multiple
                        ? (<Form.Field>
                            <Input.DropdownMulti
                                placeholder="Select immunisations"
                                dynamicOptions
                                options={immunisationTasks?.map(imm => {
                                    const immsType = this.props.showImmunisationNameAndType
                                        ? this.props.immunisationTypes?.find(it => it.id === imm.immunisationTypeId)
                                        : null;
                                    return { value: imm.value, text: immsType?.name ? `${immsType.name} - ${imm.text}` : imm.text };
                                }) || []}
                                value={this.state.values.immunisationTypeTasks}
                                showErrors={this.props.showErrors}
                                required
                                onChange={(value, isValid) => {
                                    this.setState(current => ({
                                        values: { ...current.values, immunisationTypeTasks: value },
                                        valid: { ...current.valid, immunisationTypeTasks: isValid }
                                    }), () => this.props.onChange(this.state.values, this.isValid()));
                                }}
                            />
                        </Form.Field>)
                        : (<>
                            <Input.Dropdown
                                placeholder="Select immunisations"
                                dynamicOptions
                                options={immunisationTasks?.map(imm => {
                                    const immsType = this.props.showImmunisationNameAndType
                                        ? this.props.immunisationTypes?.find(it => it.id === imm.immunisationTypeId)
                                        : null;
                                    return { value: imm.value, text: immsType?.name ? `${immsType.name} - ${imm.text}` : imm.text };
                                }) || []}
                                value={this.state.values.immunisationTypeTasks?.length > 0 ? this.state.values.immunisationTypeTasks[0] : null}
                                showErrors={this.props.showErrors}
                                required
                                onChange={(value, isValid) => {
                                    this.setState(current => ({
                                        values: { ...current.values, immunisationTypeTasks: [value] },
                                        valid: { ...current.valid, immunisationTypeTasks: isValid }
                                    }), () => this.props.onChange(this.state.values, this.isValid()));
                                }}
                            />
                            <br />
                        </>))
                }
                {!this.props.hideAssignmentOption &&
                    <Form.Field>
                        <Input.Dropdown
                            dynamicOptions
                            placeholder={"Select Users" + (this.props.assignmentOptionRequired ? " *" : "")}
                            value={this.state.values.assignedUsers?.length > 0 ? this.state.values.assignedUsers[0].id : null}
                            label={"Assigned to" + (this.props.assignmentOptionRequired ? " *" : "")}
                            showErrors={this.props.showErrors}
                            onChange={this.setAssignedUser}
                            options={this.props.adminUsers && this.props.adminUsers.map(adminUser =>
                                ({ key: adminUser.id, value: adminUser.id, text: getUserDisplayNameWithTeamAndProfession(adminUser) }))}
                            search
                            required={!!this.props.assignmentOptionRequired}
                        />
                    </Form.Field>
                }
                <br />
            </>
        );
    }
}
